// Navbar.js
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import './Navbar.css';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const downloadRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  // Close download dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const scrollToSection = (sectionId) => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);

    const doScroll = () => {
      const el = document.getElementById(sectionId);
      if (el) {
        const offset = 80; // navbar height
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    };

    if (location.pathname === '/') {
      doScroll();
    } else {
      navigate('/');
      setTimeout(doScroll, 350);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) setIsServicesOpen(false);
  };

  const toggleServices = () => setIsServicesOpen(!isServicesOpen);
  const toggleDownload = () => setIsDownloadOpen(!isDownloadOpen);

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {/* Brand Logo */}
        <div className="navbar-brand">
          <Link to="/" className="brand-logo-link">
            <img src="/img/logo.png" alt="QSlot Logo" className="logo-img" />
          </Link>
        </div>

        <div className="navbar-right">
          {/* ============================================
              DESKTOP MENU
             ============================================ */}
          <div className="desktop-menu">
            {/* Home */}
            <Link to="/" className="nav-link active">Home</Link>

            {/* Services Dropdown */}
            <div className="dropdown-container">
              <button className="dropdown-btn">
                Services <ChevronDown size={14} />
              </button>
              <div className="dropdown-menu">
                <Link to="/blogs" className="dropdown-link">
                  <span className="dropdown-link-icon">📖</span>
                  <span className="dropdown-link-text">Our Blogs</span>
                </Link>
              </div>
            </div>

            {/* How It Works — real page */}
            <Link to="/how-it-works" className="nav-link">
              How It Works
            </Link>

            {/* Why Us — scroll to #why */}
            <button
              type="button"
              className="nav-link nav-link-btn"
              onClick={() => scrollToSection('why')}
            >
              Why Us
            </button>

            {/* Contact — scroll to #contact */}
            <button
              type="button"
              className="nav-link nav-link-btn"
              onClick={() => scrollToSection('contact')}
            >
              Contact
            </button>
          </div>

          {/* ============================================
              DOWNLOAD APP DROPDOWN
             ============================================ */}
          <div className="download-dropdown" ref={downloadRef}>
            <button
              className="store-btn"
              onClick={toggleDownload}
              aria-label="Download App"
            >
              Download App{' '}
              <ChevronDown
                size={14}
                className={isDownloadOpen ? 'rotate-180' : ''}
              />
            </button>

            {isDownloadOpen && (
              <div className="download-menu">
                <a
                  href="https://apps.apple.com/us/app/qslot/id6747074785"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-link"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <div className="download-text">
                    <span className="download-sub">Download on the</span>
                    <span className="download-main">App Store</span>
                  </div>
                </a>

                <a
                  href="https://play.google.com/store/apps/details?id=com.fewas.qslot.user.app&hl=en-US"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-link"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a1.5 1.5 0 0 1-.61-1.21V3.024c0-.48.24-.91.609-1.21zM14.5 12.707l2.302 2.302-11.34 6.545 9.038-8.847zM19.79 8.51l-2.83 1.634L14.5 12l2.46 2.46 2.83 1.634a1.5 1.5 0 0 0 0-2.598v.014zM5.462 2.446L16.802 8.99 14.5 11.293 5.462 2.446z" />
                  </svg>
                  <div className="download-text">
                    <span className="download-sub">GET IT ON</span>
                    <span className="download-main">Google Play</span>
                  </div>
                </a>
              </div>
            )}
          </div>

          {/* Mobile Toggle */}
          <button
            className="icon-btn mobile-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle Menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* ============================================
          MOBILE DRAWER
         ============================================ */}
      {isMobileMenuOpen && (
        <div className="mobile-menu">
          {/* Home */}
          <Link to="/" className="mobile-link" onClick={toggleMobileMenu}>
            Home
          </Link>

          {/* Services Dropdown */}
          <div className="mobile-dropdown">
            <button onClick={toggleServices} className="mobile-dropdown-btn">
              <span>Services</span>
              <ChevronDown
                className={isServicesOpen ? 'rotate-180' : ''}
                size={16}
              />
            </button>
            {isServicesOpen && (
              <div className="mobile-sub-menu">
                <Link to="/blogs" className="mobile-sub-link" onClick={toggleMobileMenu}>
                  📖 Our Blogs
                </Link>
              </div>
            )}
          </div>

          {/* How It Works */}
          <Link to="/how-it-works" className="mobile-link" onClick={toggleMobileMenu}>
            How It Works
          </Link>

          {/* Why Us — scroll */}
          <button
            type="button"
            className="mobile-link mobile-link-btn"
            onClick={() => scrollToSection('why')}
          >
            Why Us
          </button>

          {/* Contact — scroll */}
          <button
            type="button"
            className="mobile-link mobile-link-btn"
            onClick={() => scrollToSection('contact')}
          >
            Contact
          </button>

          {/* Mobile Store Buttons */}
          <div className="store-buttons mobile-store-buttons">
            <a
              href="https://apps.apple.com/us/app/qslot/id6747074785"
              target="_blank"
              rel="noopener noreferrer"
              className="store-btn mobile-store-btn"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              <div className="store-text">
                <span className="store-sub">Download on the</span>
                <span className="store-main">App Store</span>
              </div>
            </a>

            <a
              href="https://play.google.com/store/apps/details?id=com.fewas.qslot.user.app&hl=en-US"
              target="_blank"
              rel="noopener noreferrer"
              className="store-btn mobile-store-btn"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M3.609 1.814L13.792 12 3.61 22.186a1.5 1.5 0 0 1-.61-1.21V3.024c0-.48.24-.91.609-1.21zM14.5 12.707l2.302 2.302-11.34 6.545 9.038-8.847zM19.79 8.51l-2.83 1.634L14.5 12l2.46 2.46 2.83 1.634a1.5 1.5 0 0 0 0-2.598v.014zM5.462 2.446L16.802 8.99 14.5 11.293 5.462 2.446z" />
              </svg>
              <div className="store-text">
                <span className="store-sub">GET IT ON</span>
                <span className="store-main">Google Play</span>
              </div>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;